import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import './TimesheetPDF.css';
import { createSignedTimesheet, updateSignedTimesheet, getSignedTimesheets } from '../firebase/db';
import { useAuth } from '../contexts/AuthContext';

/* ─── Robust Firestore Date Parser ─── */
const parseJobDate = (value) => {
  if (!value) return null;
  // Firestore Timestamp { seconds, nanoseconds }
  if (value && typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000);
  }
  // Already a Date
  if (value instanceof Date) return value;
  // String or number
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/* ─── Strip time for day comparison ─── */
const stripTime = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const SignatureCanvas = ({ onChange, existingDataUrl }) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [saved, setSaved] = useState(false);

  // Initialize canvas size & context once on mount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#000';
  }, []);

  // Load existing signature
  useEffect(() => {
    if (existingDataUrl && canvasRef.current) {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setHasSignature(true);
        setSaved(true);
      };
      img.src = existingDataUrl;
    }
  }, [existingDataUrl]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  // ─── iOS Safari FIX: manual touch listeners with { passive: false } ───
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e) => {
      e.preventDefault();
      isDrawingRef.current = true;
      const { x, y } = getPos(e);
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const handleTouchMove = (e) => {
      if (!isDrawingRef.current) return;
      e.preventDefault();
      const { x, y } = getPos(e);
      const ctx = canvas.getContext('2d');
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const handleTouchEnd = () => {
      if (isDrawingRef.current) {
        isDrawingRef.current = false;
        setHasSignature(true);
        setSaved(false);
        // Auto-save signature on touch end — no more two-step confusion
        const dataUrl = canvasRef.current?.toDataURL();
        if (dataUrl) onChange?.(dataUrl);
      }
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  // Mouse handlers (React synthetic events — desktop only)
  const startDrawing = (e) => {
    isDrawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawingRef.current) {
      isDrawingRef.current = false;
      setHasSignature(true);
      setSaved(false);
      // Auto-save signature on mouse up — no more two-step confusion
      const dataUrl = canvasRef.current?.toDataURL();
      if (dataUrl) onChange?.(dataUrl);
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setHasSignature(false);
    setSaved(false);
    onChange?.('');
  };

  const save = () => {
    if (!hasSignature) return;
    // Signature already auto-saved on draw end, just update visual state
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="signature-canvas-wrapper">
      <canvas
        ref={canvasRef}
        className="signature-canvas"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
      />
      <div className="signature-canvas-controls no-print">
        <button type="button" className="sig-save-btn" onClick={save} disabled={!hasSignature}>
          💾 Confirm Signature
        </button>
        <button type="button" className="sig-clear-btn" onClick={clear}>
          Clear
        </button>
        {saved && <span className="sig-saved">✓ Saved</span>}
        {hasSignature && !saved && <span className="sig-unsaved">• Auto-saved</span>}
      </div>
    </div>
  );
};

/* ─── Mobile Day Form (one day at a time) ─── */
const MobileDayForm = ({ dayIndex, day, rowDates, workEntries, units, signature, onRowDateChange, onWorkChange, onUnitChange }) => {
  const dateStr = rowDates[dayIndex] || (day ? format(day, 'yyyy-MM-dd') : '');
  const workText = workEntries[dayIndex] || '';
  const unitVal = units[dayIndex] !== undefined ? units[dayIndex] : '';
  // Manager signature: show client signature image when units is '1'
  const managerSig = unitVal === '1' && signature ? signature : null;

  return (
    <div className="mobile-day-form">
      <div className="mobile-day-date">
        <label>Date</label>
        <input
          type="date"
          className="field-input editable"
          value={dateStr}
          onChange={(e) => onRowDateChange(dayIndex, e.target.value)}
        />
      </div>
      <div className="mobile-day-work">
        <label>Work Carried Out</label>
        <textarea
          className="work-input editable"
          rows={5}
          placeholder="Describe the work carried out..."
          value={workText}
          onChange={(e) => onWorkChange(dayIndex, e.target.value)}
        />
      </div>
      <div className="mobile-day-footer">
        <div className="mobile-unit-field">
          <label>Units</label>
          <input
            className="unit-input editable"
            type="text"
            placeholder="1"
            value={unitVal}
            onChange={(e) => onUnitChange(dayIndex, e.target.value)}
          />
        </div>
        <div className="mobile-sig-field">
          <label>Manager Signature</label>
          {managerSig ? (
            <img src={managerSig} alt="Manager signature" className="manager-sig-img" />
          ) : (
            <div className="manager-sig-line"></div>
          )}
        </div>
      </div>
    </div>
  );
};

const TimesheetPDF = ({ job, timesheets, existingSignedTs }) => {
  const { user, profile } = useAuth();
  const [workEntries, setWorkEntries] = useState({});
  const [units, setUnits] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // Editable single-day fields
  const [clientName, setClientName] = useState(job.companyName || job.clientContact || '');
  const [sheetDate, setSheetDate] = useState(
    job.scheduledDate ? format(parseJobDate(job.scheduledDate) || new Date(), 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy')
  );
  const [siteAddress, setSiteAddress] = useState(job.location || job.companyAddress || '');
  const [contactName, setContactName] = useState(job.primarySiteContact || '');
  const [technician, setTechnician] = useState(
    Array.isArray(job.assignedToName) ? job.assignedToName.join(', ') : job.assignedToName || ''
  );
  const [repairs, setRepairs] = useState(job.jobType || '');
  const [plotRoom, setPlotRoom] = useState(job.jobReference || '');
  const [workCarriedOut, setWorkCarriedOut] = useState('');

  // Editable multi-day row dates
  const [rowDates, setRowDates] = useState({});
  const datesInitialized = useRef(false);

  // Signature fields
  const [signature, setSignature] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerDate, setSignerDate] = useState(format(new Date(), 'dd/MM/yyyy'));
  const [activeDay, setActiveDay] = useState(0);

  // Load existing signed timesheet data into form fields
  useEffect(() => {
    if (!existingSignedTs) return;
    const ts = existingSignedTs;
    if (ts.clientName) setClientName(ts.clientName);
    if (ts.sheetDate) setSheetDate(ts.sheetDate);
    if (ts.siteAddress) setSiteAddress(ts.siteAddress);
    if (ts.contactName) setContactName(ts.contactName);
    if (ts.technician) setTechnician(ts.technician);
    if (ts.repairs) setRepairs(ts.repairs);
    if (ts.plotRoom) setPlotRoom(ts.plotRoom);
    if (ts.workCarriedOut) setWorkCarriedOut(ts.workCarriedOut);
    if (ts.signature) setSignature(ts.signature);
    if (ts.signerName) setSignerName(ts.signerName);
    if (ts.signerDate) setSignerDate(ts.signerDate);
    if (ts.workEntries) setWorkEntries(ts.workEntries);
    if (ts.units) setUnits(ts.units);
    // Load row dates from saved days
    if (ts.days && Array.isArray(ts.days)) {
      const loadedDates = {};
      ts.days.forEach((dayStr, idx) => {
        try {
          const d = new Date(dayStr);
          if (!isNaN(d.getTime())) {
            loadedDates[idx] = format(d, 'yyyy-MM-dd');
          }
        } catch (e) { /* ignore invalid dates */ }
      });
      if (Object.keys(loadedDates).length > 0) {
        setRowDates(loadedDates);
        datesInitialized.current = true;
      }
    }
  }, [existingSignedTs]);

  const isMultiDay = job.isMultiDay || job.scheduledEndDate;

  const startDate = parseJobDate(job.scheduledDate);
  const endDate = parseJobDate(job.scheduledEndDate);
  const days = [];
  if (isMultiDay && startDate && endDate) {
    const s = stripTime(startDate);
    const e = stripTime(endDate);
    let current = new Date(s);
    let count = 0;
    const maxDays = 14;
    while (current.getTime() <= e.getTime() && count < maxDays) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
      count++;
    }
  }

  // Initialize editable row dates and prepopulate first row units
  useEffect(() => {
    if (isMultiDay && days.length > 0 && !datesInitialized.current) {
      datesInitialized.current = true;
      const initialDates = {};
      days.forEach((day, idx) => {
        initialDates[idx] = format(day, 'yyyy-MM-dd');
      });
      setRowDates(initialDates);
      // Prepopulate first row units with "1"
      setUnits(prev => {
        if (prev[0] === undefined || prev[0] === '' || prev[0] === null) {
          return { ...prev, 0: '1' };
        }
        return prev;
      });
    }
  }, [isMultiDay, days.length]);

  const handleWorkEntryChange = (dayIndex, value) => {
    setWorkEntries(prev => ({ ...prev, [dayIndex]: value }));
  };

  const handleUnitChange = (dayIndex, value) => {
    setUnits(prev => ({ ...prev, [dayIndex]: value }));
  };

  const handleRowDateChange = (idx, value) => {
    setRowDates(prev => ({ ...prev, [idx]: value }));
  };

  const handlePrint = () => {
    const previewEl = document.querySelector('.timesheet-preview');
    if (!previewEl) return;

    // ── Clone for print manipulation ──
    const clone = previewEl.cloneNode(true);
    const originalCanvases = previewEl.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');

    // Convert each canvas to an <img>
    originalCanvases.forEach((origCanvas, idx) => {
      const cloneCanvas = cloneCanvases[idx];
      if (!cloneCanvas) return;
      try {
        const dataUrl = origCanvas.toDataURL('image/png');
        const img = document.createElement('img');
        img.src = dataUrl;
        img.style.width = '300px';
        img.style.height = '60px';
        img.style.border = '1px solid #999';
        img.style.background = 'transparent';
        img.style.display = 'block';
        img.className = 'signature-canvas';
        cloneCanvas.parentNode.replaceChild(img, cloneCanvas);
      } catch (e) {
        console.error('Canvas to image failed:', e);
      }
    });

    // Fix logo paths to absolute URL
    const logoImages = clone.querySelectorAll('img[src="/Gold.png"], img[src="Gold.png"]');
    logoImages.forEach(img => {
      img.src = `${window.location.origin}/Gold.png`;
    });

    // ── Convert date inputs to plain text spans (no calendar icon in print) ──
    const dateInputs = clone.querySelectorAll('input[type="date"]');
    dateInputs.forEach(input => {
      const val = input.value?.trim();
      let displayDate = val || 'dd/mm/yyyy';
      if (val) {
        try {
          const [y, m, d] = val.split('-');
          if (y && m && d) displayDate = `${d}/${m}/${y}`;
        } catch (e) {}
      }
      const span = document.createElement('span');
      span.className = 'field-input print-text';
      span.textContent = displayDate;
      input.parentNode.replaceChild(span, input);
    });

    // ── Convert header table inputs to plain text spans (no truncation, allow wrapping) ──
    const headerInputs = clone.querySelectorAll('.header-table input');
    headerInputs.forEach(input => {
      const span = document.createElement('span');
      span.className = 'field-input print-text';
      span.textContent = input.value || '';
      span.style.whiteSpace = 'normal';
      span.style.wordWrap = 'break-word';
      input.parentNode.replaceChild(span, input);
    });

    // ── Convert textareas in data-table to plain text spans ──
    const dataTable = clone.querySelector('.data-table');
    if (dataTable) {
      const textareas = dataTable.querySelectorAll('textarea');
      textareas.forEach(ta => {
        const span = document.createElement('span');
        span.className = 'work-input print-text';
        span.textContent = ta.value || '';
        ta.parentNode.replaceChild(span, ta);
      });
      const unitInputs = dataTable.querySelectorAll('input.unit-input');
      unitInputs.forEach(input => {
        const span = document.createElement('span');
        span.className = 'unit-input print-text';
        span.textContent = input.value || '';
        input.parentNode.replaceChild(span, input);
      });
    }

    // ── REMOVE EMPTY ROWS from the data-table ──
    // Remove any row where units field is empty/null
    if (dataTable) {
      const tbody = dataTable.querySelector('tbody');
      if (tbody) {
        const rows = tbody.querySelectorAll('tr');
        rows.forEach(row => {
          const unitSpan = row.querySelector('.unit-input.print-text');
          const unitVal = unitSpan ? unitSpan.textContent?.trim() : '';
          // Remove row if units is empty
          if (!unitVal) {
            row.remove();
          }
        });
      }
    }

    const timesheetContent = clone.innerHTML;

    const printStyles = `
      @page { size: A4 portrait; margin: 0; }
      html, body { margin: 0; padding: 10mm; font-family: Arial, sans-serif; font-size: 10pt; color: #000; background: white; }
      .no-print, .timesheet-controls, .signature-canvas-controls,
      .mobile-header-cards, .mobile-day-tabs-wrap, .mobile-no-days { display: none !important; }

      .single-day .page { border: 1px solid #000; padding: 15mm; }
      .single-day .doc-version { text-align: right; font-size: 9pt; margin-bottom: 10px; }
      .single-day .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 8px; }
      .single-day .company-name { font-size: 14pt; font-weight: bold; text-transform: uppercase; }
      .single-day .company-details { font-size: 9pt; margin-top: 2px; }
      .single-day .field-row { display: flex; align-items: baseline; margin-bottom: 8px; width: 100%; }
      .single-day .field-row.half { width: 50%; padding-right: 8px; box-sizing: border-box; }
      .single-day .field-label { font-weight: bold; font-size: 10.5pt; white-space: nowrap; flex-shrink: 0; }
      .single-day .field-input { flex: 1; border: none; border-bottom: 1px solid #000; padding: 2px 4px; font-family: inherit; font-size: 10pt; background: transparent; }
      .single-day .work-area { margin-top: 4px; margin-bottom: 10px; }
      .single-day .work-input { width: 100%; min-height: 40px; border: 1px solid #000; padding: 6px; font-family: inherit; font-size: 10pt; resize: none; background: transparent; }
      .single-day .certification-header { text-align: center; font-weight: bold; font-size: 10pt; margin: 15px 0 8px; }
      .single-day .certification-text { font-size: 10pt; line-height: 1.4; text-align: justify; border: 1px solid #000; padding: 10px; }
      .single-day .signature-row { display: flex; align-items: baseline; margin-bottom: 8px; }
      .single-day .signature-label { font-weight: bold; font-size: 10.5pt; width: 60px; flex-shrink: 0; }
      .single-day .signature-field { flex: 1; position: relative; min-height: 30px; }
      .single-day .signature-input { flex: 1; border: none; border-bottom: 1px solid #000; padding: 2px 4px; font-family: inherit; font-size: 10pt; background: transparent; }
      .single-day .signature-canvas { border: 1px solid #999; background: transparent; width: 300px; height: 60px; display: block; }
      .single-day .signature-print-line { position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: #000; }

      .multi-day .header-table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
      .multi-day .header-table td { vertical-align: bottom; padding: 8px 12px; border: 1px solid #999; height: 50px; }
      .multi-day .header-table td:first-child { width: 20%; }
      .multi-day .header-table td:nth-child(2) { width: 30%; }
      .multi-day .header-table td:nth-child(3) { width: 30%; }
      .multi-day .header-table td:last-child { width: 20%; }
      .multi-day .header-label { font-weight: bold; font-size: 11pt; }
      .multi-day .logo-cell { text-align: center; vertical-align: middle !important; }
      .multi-day .logo-cell img { max-width: 80px; max-height: 60px; }
      .multi-day .field-input.print-text { display: inline-block; border: none; border-bottom: 1px solid #000; padding: 2px 4px; font-family: inherit; font-size: 10pt; background: transparent; white-space: normal; word-wrap: break-word; max-width: 100%; }
      .multi-day .data-table { width: 100%; border-collapse: collapse; margin-top: 0; table-layout: fixed; }
      .multi-day .data-table th { background: #777 !important; color: white; font-weight: normal; font-size: 10.5pt; text-align: center; padding: 10px 8px; border: 1px solid #999; height: 40px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .multi-day .data-table td { border: 1px solid #999; padding: 8px; height: 45px; vertical-align: middle; text-align: center; }
      .multi-day .data-table td:nth-child(2) { text-align: left; }
      .multi-day .data-table tr { border-bottom: 1px solid #999; }
      .multi-day .field-input { border: none; border-bottom: 1px solid #000; padding: 2px 4px; font-family: inherit; font-size: 10pt; background: transparent; }
      .multi-day .work-input.print-text { width: 100%; min-height: 30px; border: none; border-bottom: 1px solid #000; padding: 4px; font-family: inherit; font-size: 10pt; background: transparent; display: block; text-align: left; white-space: pre-wrap; word-wrap: break-word; }
      .multi-day .unit-input.print-text { width: 100%; border: none; border-bottom: 1px solid #000; padding: 4px; font-family: inherit; font-size: 10pt; text-align: center; background: transparent; display: block; }
      .multi-day .manager-sig-img { max-width: 80px; max-height: 30px; display: block; margin: 0 auto; }
      .multi-day .empty-row td { color: #333; }
      .multi-day .empty-row td input, .multi-day .empty-row td textarea { color: #999; }
      .multi-day .multi-day-signature { margin-top: 15px; border-top: 2px solid #000; padding-top: 12px; }
      .multi-day .certification-header { text-align: center; font-weight: bold; font-size: 10pt; margin: 15px 0 8px; }
      .multi-day .certification-text { font-size: 10pt; line-height: 1.4; border: 1px solid #000; padding: 10px; }
      .multi-day .signature-row { display: flex; align-items: baseline; margin-bottom: 8px; }
      .multi-day .signature-label { font-weight: bold; font-size: 10.5pt; width: 60px; flex-shrink: 0; }
      .multi-day .signature-field { flex: 1; position: relative; min-height: 30px; }
      .multi-day .signature-input { flex: 1; border: none; border-bottom: 1px solid #000; padding: 2px 4px; font-family: inherit; font-size: 10pt; background: transparent; }
      .multi-day .signature-canvas { border: 1px solid #999; background: transparent; width: 300px; height: 60px; display: block; }
      .multi-day .signature-print-line { position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: #000; }
      .manager-sig-line { border-bottom: 1px solid #000; min-height: 30px; width: 100%; display: block; }

      input, textarea { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      img { max-width: 100%; height: auto; }
    `;

    const printHtml = `
      <html><head><style>${printStyles}</style></head>
      <body class="${isMultiDay ? 'multi-day' : 'single-day'}">
        ${timesheetContent}
      </body></html>
    `;

    // FAST PRINT: swap body content temporarily
    const originalBody = document.body.innerHTML;
    const originalOverflow = document.body.style.overflow;
    document.body.innerHTML = printHtml;
    document.body.style.overflow = 'visible';

    window.print();

    setTimeout(() => {
      document.body.innerHTML = originalBody;
      document.body.style.overflow = originalOverflow;
      window.location.reload();
    }, 100);
  };

  const handleSaveSigned = async () => {
    if (!signature) {
      alert('Please add a signature before saving.');
      return;
    }
    setSaving(true);
    console.log('[TimesheetPDF] Saving signed timesheet...');
    console.log('[TimesheetPDF] Signature length:', signature?.length);
    console.log('[TimesheetPDF] Signature starts with:', signature?.substring(0, 60));

    // Build days from rowDates (editable) or fallback to generated days
    const savedDays = [];
    const allIndices = [...Array(days.length + (7 - days.length)).keys()];
    for (const idx of allIndices) {
      const dateStr = rowDates[idx];
      if (dateStr) {
        savedDays.push(new Date(dateStr + 'T00:00:00').toISOString());
      } else if (idx < days.length) {
        savedDays.push(days[idx].toISOString());
      }
    }

    const data = {
      jobId: job.id,
      jobTitle: job.title,
      type: isMultiDay ? 'multi-day' : 'single-day',
      clientName,
      sheetDate,
      siteAddress,
      contactName,
      technician,
      repairs,
      plotRoom,
      workCarriedOut,
      signature,
      signerName,
      signerDate,
      workEntries,
      units,
      days: savedDays,
      isMultiDay,
      createdBy: user?.uid,
      createdByName: user?.displayName || profile?.displayName || '',
    };

    // If a signed timesheet already exists for this job, UPDATE it instead of creating a new one
    let result;
    if (existingSignedTs?.id) {
      console.log('[TimesheetPDF] Updating existing signed timesheet:', existingSignedTs.id);
      result = await updateSignedTimesheet(existingSignedTs.id, data);
      if (!result.error) {
        result.id = existingSignedTs.id; // preserve ID for success message
      }
    } else {
      console.log('[TimesheetPDF] Creating new signed timesheet');
      result = await createSignedTimesheet(data);
    }

    setSaving(false);
    if (result.error) {
      console.error('[TimesheetPDF] Save error:', result.error);
      setSavedMsg('Error saving: ' + result.error);
    } else {
      console.log('[TimesheetPDF] Saved successfully! ID:', result.id);
      setSavedMsg('✓ Timesheet saved! ID: ' + result.id);
      setTimeout(() => setSavedMsg(''), 3000);
    }
  };

  return (
    <div className="timesheet-pdf-wrapper">
      <div className="timesheet-controls no-print">
        <button className="btn btn-primary print-btn" onClick={handlePrint}>
          🖨️ Print / Save as PDF
        </button>
        <span className="mode-badge">{isMultiDay ? 'Multi-Day' : 'Single-Day'}</span>
        <button
          className="btn btn-success save-btn"
          onClick={handleSaveSigned}
          disabled={saving}
        >
          {saving ? 'Saving...' : '💾 Save & Sign'}
        </button>
        {savedMsg && <span className="saved-msg">{savedMsg}</span>}
      </div>

      <div className="timesheet-preview">
        {!isMultiDay ? (
          <div className="single-day">
            <div className="page">
              <div className="doc-version">Document version 3.0</div>

              <div className="header">
                <div className="company-name">CITYSURFACEREPAIRS LTD</div>
                <div className="company-details">
                  124 CITY ROAD, LONDON, EC1V 2NX
                  <span> | </span>
                  info@citysurfacerepairs.com
                  <span> | </span>
                  Registered Company Number 13541974
                </div>
              </div>

              <div className="form-section">
                <div className="field-row half">
                  <span className="field-label">Client:</span>
                  <input
                    className="field-input editable"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                  />
                </div>
                <div className="field-row half">
                  <span className="field-label">Date:</span>
                  <input
                    className="field-input editable"
                    value={sheetDate}
                    onChange={(e) => setSheetDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="field-row">
                <span className="field-label">Site Address:</span>
                <input
                  className="field-input editable"
                  value={siteAddress}
                  onChange={(e) => setSiteAddress(e.target.value)}
                />
              </div>

              <div className="field-row">
                <span className="field-label">Contact Name and Position:</span>
                <input
                  className="field-input editable"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </div>

              <div className="field-row">
                <span className="field-label">Technician Attending:</span>
                <input
                  className="field-input editable"
                  value={technician}
                  onChange={(e) => setTechnician(e.target.value)}
                />
              </div>

              <div className="field-row">
                <span className="field-label">Repairs Requested:</span>
                <input
                  className="field-input editable"
                  value={repairs}
                  onChange={(e) => setRepairs(e.target.value)}
                />
              </div>

              <div className="field-row">
                <span className="field-label">Plot/Room numbers (where applicable):</span>
                <input
                  className="field-input editable"
                  value={plotRoom}
                  onChange={(e) => setPlotRoom(e.target.value)}
                />
              </div>

              <div className="work-area">
                <div className="field-label">Work Carried Out:</div>
                <textarea
                  className="work-input editable"
                  rows={4}
                  placeholder="Enter work carried out..."
                  value={workCarriedOut}
                  onChange={(e) => setWorkCarriedOut(e.target.value)}
                />
              </div>

              <div className="certification-header">
                Please Ask Customer / Site Contact To Read And Sign
              </div>

              <div className="certification-text">
                <strong>STATEMENT READ</strong> - I hereby certify that I have fully inspected the above works and can confirm the works have been carried out in full, and that all work areas have been cleaned thoroughly. I/We are 100% satisfied with the repairs/service and authorize payment to be made directly to City Surface Repairs Ltd.
              </div>

              <div className="signature-section">
                <div className="signature-row">
                  <span className="signature-label">Signed:</span>
                  <div className="signature-field">
                    <SignatureCanvas onChange={setSignature} existingDataUrl={signature} />
                    {!signature && <span className="signature-print-line"></span>}
                  </div>
                </div>
                <div className="signature-row">
                  <span className="signature-label">Name:</span>
                  <input
                    className="signature-input editable"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Enter name..."
                  />
                </div>
                <div className="signature-row">
                  <span className="signature-label">Date:</span>
                  <input
                    className="signature-input editable"
                    value={signerDate}
                    onChange={(e) => setSignerDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="multi-day">
            {/* Mobile header cards */}
            <div className="mobile-header-cards">
              <div className="mobile-header-card">
                <span className="header-label">Client</span>
                <input className="field-input editable" value={clientName} onChange={(e) => setClientName(e.target.value)} />
              </div>
              <div className="mobile-header-card">
                <span className="header-label">Site Address</span>
                <input className="field-input editable" value={siteAddress} onChange={(e) => setSiteAddress(e.target.value)} />
              </div>
              <div className="mobile-header-card">
                <span className="header-label">Operative</span>
                <input className="field-input editable" value={technician} onChange={(e) => setTechnician(e.target.value)} />
              </div>
              <div className="mobile-logo">
                <img src="/Gold.png" alt="Logo" />
              </div>
            </div>

            {/* Mobile day tabs + active day form */}
            {days.length > 0 ? (
              <div className="mobile-day-tabs-wrap">
                <div className="mobile-day-tabs">
                  {days.map((d, idx) => {
                    const dayNum = idx + 1;
                    const dateLabel = d ? format(d, 'EEE d') : `Day ${dayNum}`;
                    const hasWork = !!workEntries[idx];
                    return (
                      <button
                        key={idx}
                        className={`day-tab ${activeDay === idx ? 'active' : ''} ${hasWork ? 'has-work' : ''}`}
                        onClick={() => setActiveDay(idx)}
                        type="button"
                      >
                        <span className="tab-day">{dateLabel}</span>
                        {hasWork && <span className="tab-dot">•</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="mobile-day-counter">
                  Day {activeDay + 1} of {days.length}
                </div>
                <MobileDayForm
                  dayIndex={activeDay}
                  day={days[activeDay]}
                  rowDates={rowDates}
                  workEntries={workEntries}
                  units={units}
                  signature={signature}
                  onRowDateChange={handleRowDateChange}
                  onWorkChange={handleWorkEntryChange}
                  onUnitChange={handleUnitChange}
                />
              </div>
            ) : (
              <div className="mobile-no-days">
                <p>⚠️ No days found for this multi-day job.</p>
                <p className="mobile-no-days-hint">
                  Scheduled: {startDate ? format(startDate, 'dd/MM/yyyy') : '—'} → {endDate ? format(endDate, 'dd/MM/yyyy') : '—'}
                </p>
              </div>
            )}

            {/* Desktop table (hidden on mobile) */}
            <table className="header-table">
              <tbody>
                <tr>
                  <td>
                    <span className="header-label">Client:</span><br/>
                    <input className="field-input editable" value={clientName} onChange={(e) => setClientName(e.target.value)} />
                  </td>
                  <td>
                    <span className="header-label">Site Address:</span><br/>
                    <input className="field-input editable" value={siteAddress} onChange={(e) => setSiteAddress(e.target.value)} />
                  </td>
                  <td>
                    <span className="header-label">Operative:</span><br/>
                    <input className="field-input editable" value={technician} onChange={(e) => setTechnician(e.target.value)} />
                  </td>
                  <td className="logo-cell">
                    <img src="/Gold.png" alt="Logo" />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>Date</th>
                  <th style={{ width: '45%' }}>Work Carried Out</th>
                  <th style={{ width: '10%' }}>Units</th>
                  <th style={{ width: '30%' }}>Manager Signature</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day, idx) => {
                  const workText = workEntries[idx] || '';
                  const unitVal = units[idx] !== undefined ? units[idx] : '';
                  return (
                    <tr key={idx}>
                      <td>
                        <input
                          type="date"
                          className="field-input editable"
                          value={rowDates[idx] || format(day, 'yyyy-MM-dd')}
                          onChange={(e) => handleRowDateChange(idx, e.target.value)}
                        />
                      </td>
                      <td>
                        <textarea
                          className="work-input editable"
                          rows={2}
                          placeholder="Enter work description..."
                          value={workText}
                          onChange={(e) => handleWorkEntryChange(idx, e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="unit-input editable"
                          type="text"
                          placeholder=""
                          value={unitVal}
                          onChange={(e) => handleUnitChange(idx, e.target.value)}
                        />
                      </td>
                      <td>
                        {unitVal === '1' && signature ? (
                          <img src={signature} alt="Manager signature" className="manager-sig-img" />
                        ) : (
                          <div className="manager-sig-line"></div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {days.length < 7 && Array.from({ length: 7 - days.length }).map((_, idx) => {
                  const emptyIdx = days.length + idx;
                  const unitVal = units[emptyIdx] !== undefined ? units[emptyIdx] : '';
                  return (
                    <tr key={`empty-${idx}`} className="empty-row">
                      <td>
                        <input
                          type="date"
                          className="field-input editable"
                          value={rowDates[emptyIdx] || ''}
                          onChange={(e) => handleRowDateChange(emptyIdx, e.target.value)}
                        />
                      </td>
                      <td>
                        <textarea
                          className="work-input editable"
                          rows={2}
                          placeholder="Enter work description..."
                          value={workEntries[emptyIdx] || ''}
                          onChange={(e) => handleWorkEntryChange(emptyIdx, e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="unit-input editable"
                          type="text"
                          placeholder=""
                          value={unitVal}
                          onChange={(e) => handleUnitChange(emptyIdx, e.target.value)}
                        />
                      </td>
                      <td>
                        {unitVal === '1' && signature ? (
                          <img src={signature} alt="Manager signature" className="manager-sig-img" />
                        ) : (
                          <div className="manager-sig-line"></div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Multi-day signature section */}
            <div className="multi-day-signature">
              <div className="certification-header">
                Please Ask Customer / Site Contact To Read And Sign
              </div>
              <div className="certification-text">
                <strong>STATEMENT READ</strong> - I hereby certify that I have fully inspected the above works and can confirm the works have been carried out in full, and that all work areas have been cleaned thoroughly. I/We are 100% satisfied with the repairs/service and authorize payment to be made directly to City Surface Repairs Ltd.
              </div>
              <div className="signature-section">
                <div className="signature-row">
                  <span className="signature-label">Signed:</span>
                  <div className="signature-field">
                    <SignatureCanvas onChange={setSignature} existingDataUrl={signature} />
                    {!signature && <span className="signature-print-line"></span>}
                  </div>
                </div>
                <div className="signature-row">
                  <span className="signature-label">Name:</span>
                  <input
                    className="signature-input editable"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Enter name..."
                  />
                </div>
                <div className="signature-row">
                  <span className="signature-label">Date:</span>
                  <input
                    className="signature-input editable"
                    value={signerDate}
                    onChange={(e) => setSignerDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimesheetPDF;
